import { isExpired, isVersionCompatible } from './helpers.js'

const logError = (operation, error) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[INDEXEDDB] ${operation}:`, error)
  }
}

export const deleteRecord = async (table, id) => {
  try {
    await table.delete(id)
  } catch (error) {
    logError('deleteRecord', error)
  }
}

export const saveRecord = async (table, id, data, version = 1, metadata = {}) => {
  try {
    await table.put({
      id,
      data,
      savedAt: Date.now(),
      version,
      metadata
    })
  } catch (error) {
    logError('saveRecord', error)
  }
}

export const getRecord = async (table, id, currentVersion = 1, maxDays = 7) => {
  try {
    const record = await table.get(id)
    if (!record) return null

    const isInvalid = !isVersionCompatible(record.version, currentVersion) ||
      isExpired(record.savedAt, maxDays)

    if (isInvalid) {
      await deleteRecord(table, id)
      return null
    }

    return record
  } catch (error) {
    logError('getRecord', error)
    return null
  }
}

const createTimeoutPromise = (timeoutMs) => {
  let timeoutId
  const promise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('IndexedDB timeout'))
    }, timeoutMs)
  })
  return {
    promise,
    clear: () => clearTimeout(timeoutId)
  }
}

export const getRecordWithTimeout = async (
  table,
  id,
  currentVersion = 1,
  maxDays = 7,
  timeoutMs = 1000
) => {
  const timeout = createTimeoutPromise(timeoutMs)
  try {
    const record = await Promise.race([
      table.get(id),
      timeout.promise
    ])
    timeout.clear()
    if (!record) return null

    const isInvalid = !isVersionCompatible(record.version, currentVersion) ||
      isExpired(record.savedAt, maxDays)

    if (isInvalid) {
      deleteRecord(table, id)
      return null
    }

    return record
  } catch (error) {
    timeout.clear()
    logError('getRecordWithTimeout', error)
    return null
  }
}

export const clearTable = async (table) => {
  try {
    await table.clear()
  } catch (error) {
    logError('clearTable', error)
  }
}

export const cleanExpiredRecords = async (table, maxDays, timestampField = 'savedAt') => {
  try {
    const records = await table.toArray()
    const expiredIds = records
      .filter(record => isExpired(record[timestampField], maxDays))
      .map(record => record.id)

    if (expiredIds.length > 0) {
      await table.bulkDelete(expiredIds)
      return expiredIds.length
    }

    return 0
  } catch (error) {
    logError('cleanExpiredRecords', error)
    return 0
  }
}
